import { CommessaLink } from "../components/EntityLink";
import InsightButton from "../components/InsightButton";
import React, { useEffect, useState, useRef } from "react";
import { collection, query, where, onSnapshot, getDocs, getDoc, doc as fsDoc, updateDoc } from "firebase/firestore";
import { db } from "../firebase";
import { useAuth } from "../contexts/AuthContext";
import { useOrg } from "../contexts/OrgContext";
import { isTaskOverdue, daysUntilDue } from "../utils/taskHelpers";
import { CheckCircle, Folder, ChevronDown, ChevronRight, MoreVertical, EyeOff, ArrowLeft, ArrowRight, Plus } from "lucide-react";
import TaskDetailPanel from "../components/TaskDetailPanel";
import SubtaskDetailPanel from "../components/SubtaskDetailPanel";
import { useToast } from "../utils/Toast";
import { toLocalDateStr } from "../utils/dateUtils";

// Colonne disponibili oltre a "Nome" (sempre fissa, prima, non nascondibile).
// Ogni voce sa disegnare da sola la propria cella — così aggiungere una colonna
// nuova in futuro vuol dire aggiungere una riga qui, non toccare il resto della pagina.
const COLONNE_DEF = {
  commessa: {
    label: "Commessa", width: 160,
    render: (task, ctx) => task.projectId && ctx.projects[task.projectId] ? (
      <div style={{ display:"inline-flex", alignItems:"center", gap:5, maxWidth:"100%" }}>
        <div style={{ width:8, height:8, borderRadius:2, flexShrink:0,
          background: ctx.projects[task.projectId].color || "#7c3aed" }}/>
        <span style={{ fontSize:12, color:"#6b7280", overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>
          {ctx.projects[task.projectId].codiceCommessa || ctx.projects[task.projectId].name}
        </span>
      </div>
    ) : <span style={{ fontSize:12, color:"#d1d5db" }}>—</span>,
  },
  stato: {
    label: "Stato", width: 120,
    render: (task) => task.status==="sospesa" ? (
      <span style={{ fontSize:11, color:"#d97706", fontWeight:600 }}>⏸ Sospesa</span>
    ) : task.status==="annullata" ? (
      <span style={{ fontSize:11, color:"#dc2626", fontWeight:600 }}>🚫 Annullata</span>
    ) : <span style={{ fontSize:11, color:"#d1d5db" }}>—</span>,
  },
  dataScadenza: {
    label: "Data di scadenza", width: 160,
    render: (task, ctx) => {
      const date = ctx.fmtDate(task);
      return <span style={{ fontSize:12, fontWeight:600, color:date?.color||"#d1d5db" }}>{date?.label||"—"}</span>;
    },
  },
  priorita: {
    label: "Priorità", width: 110,
    render: (task) => {
      const p = task.priority || "media";
      const cfg = { alta:{label:"Alta",col:"#dc2626"}, media:{label:"Media",col:"#d97706"}, bassa:{label:"Bassa",col:"#059669"} }[p];
      return <span style={{ fontSize:12, fontWeight:600, color:cfg.col }}>{cfg.label}</span>;
    },
  },
  sezione: {
    label: "Sezione", width: 160,
    render: (task, ctx) => {
      const nome = ctx.sezioni[task.sectionId];
      return nome ? <span style={{ fontSize:12, color:"#6b7280", overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{nome}</span>
        : <span style={{ fontSize:12, color:"#d1d5db" }}>—</span>;
    },
  },
  tag: {
    label: "Etichette", width: 160,
    render: (task) => task.tags?.length ? (
      <span style={{ fontSize:11, color:"#7c3aed", overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>
        {task.tags.join(", ")}
      </span>
    ) : <span style={{ fontSize:12, color:"#d1d5db" }}>—</span>,
  },
};
const COLONNE_DEFAULT = ["commessa","stato","dataScadenza"];

export default function MyTasksPage({ onNavigate }) {
  const { currentUser } = useAuth();
  const { effectiveOrgId: orgId } = useOrg();
  const [tasks, setTasks]       = useState([]);
  const [projects, setProjects] = useState({});
  const [orgInfo, setOrgInfo] = useState(null);
  const { addToast } = useToast();
  const [sezioni, setSezioni]   = useState({}); // sectionId -> nome
  const [selectedTask, setSelectedTask]       = useState(null);
  const [selectedSubtask, setSelectedSubtask] = useState(null);
  const [groupBy, setGroupBy]   = useState("progetto");
  const [collapsed, setCollapsed] = useState({});
  const [search, setSearch] = useState("");
  const [colonne, setColonne] = useState(COLONNE_DEFAULT); // colonne visibili, in ordine, oltre a Nome
  const [menuColonna, setMenuColonna] = useState(null); // id colonna con menu aperto, o "+"
  const menuRef = useRef(null);
  const today = toLocalDateStr(new Date());

  // Preferenza colonne salvata per utente — così la trovi come l'hai lasciata la volta dopo,
  // anche da un altro dispositivo. Un solo campo piccolo, cambia raramente.
  useEffect(() => {
    if (!currentUser) return;
    getDoc(fsDoc(db,"users",currentUser.uid)).then(snap => {
      const salvate = snap.exists() ? snap.data().prefsColonneAttivita : null;
      if (Array.isArray(salvate) && salvate.every(c => COLONNE_DEF[c])) setColonne(salvate);
    });
  }, [currentUser]);

  const salvaColonne = nuove => {
    setColonne(nuove);
    if (currentUser) updateDoc(fsDoc(db,"users",currentUser.uid), { prefsColonneAttivita: nuove }).catch(()=>{});
  };

  // Chiude il menu colonna al click fuori
  useEffect(() => {
    if (!menuColonna) return;
    const chiudi = e => { if (menuRef.current && !menuRef.current.contains(e.target)) setMenuColonna(null); };
    document.addEventListener("mousedown", chiudi);
    return () => document.removeEventListener("mousedown", chiudi);
  }, [menuColonna]);

  const [soloCondivise, setSoloCondivise] = useState(false);
  const nCondivise = tasks.filter(t=>t._condivisa).length;
  // se non ci sono più attività condivise il filtro si spegne da solo (il pulsante sparisce)
  useEffect(() => { if (nCondivise === 0 && soloCondivise) setSoloCondivise(false); }, [nCondivise, soloCondivise]);
  const tasksBase = soloCondivise && nCondivise > 0 ? tasks.filter(t=>t._condivisa) : tasks;
  const tasksFiltrati = search.trim()
    ? tasksBase.filter(t => t.title?.toLowerCase().includes(search.toLowerCase()) ||
        t.assignedName?.toLowerCase().includes(search.toLowerCase()) ||
        (projects[t.projectId]?.name||"").toLowerCase().includes(search.toLowerCase()))
    : tasksBase;

  // La stampa rispecchia la schermata: stessa modalità di raggruppamento scelta ("Per
  // progetto" oppure "Per scadenza"), con un blocco separato per ogni commessa e
  // l'intestazione che si ripete a ogni cambio pagina, così si sa sempre a quale commessa
  // appartengono le righe che si stanno leggendo.
  const stampaPDF = () => {
    const win = window.open("","_blank");
    const esc = v => String(v ?? "").replace(/&/g,"&amp;").replace(/</g,"&lt;").replace(/>/g,"&gt;");
    const logoHtml = orgInfo?.logo ? `<img src="${orgInfo.logo}" style="height:36px;object-fit:contain;"/>` :
      `<div style="font-size:16px;font-weight:900;color:#1e1b4b;">◆ ${esc(orgInfo?.nome||orgInfo?.name||"")}</div>`;
    const fmtData = d => d ? d.split("-").reverse().join("/") : "—";
    const statoDi = t => t.status==="sospesa" ? {txt:"Sospesa",col:"#d97706",forte:true}
      : t.status==="annullata" ? {txt:"Annullata",col:"#dc2626",forte:true}
      : isTaskOverdue(t) ? {txt:"In ritardo",col:"#dc2626",forte:true}
      : {txt:"Attiva",col:"#6b7280",forte:false};
    const perScadenzaOrd = (x,y) => (x.dueDate||"9999").localeCompare(y.dueDate||"9999") || (x.title||"").localeCompare(y.title||"","it");
    const nomeCommessa = pr => pr ? (pr.codiceCommessa ? `${pr.codiceCommessa} — ${pr.name}` : pr.name) : "Senza commessa";

    const riga = (t, conCommessa) => {
      const st = statoDi(t);
      const pr = projects[t.projectId];
      const sub = !!t._isSubtask;
      const chiusa = t.status==="completata" || t.status==="annullata";
      return `<tr>
        <td style="padding-left:${sub?24:8}px">${sub?'<span class="sub">&#8627;</span> ':''}<span${chiusa?' class="off"':''}>${esc(t.title||"—")}</span>${sub&&t.parentTaskTitle?` <span class="dip">di «${esc(t.parentTaskTitle)}»</span>`:""}</td>
        ${conCommessa?`<td>${esc(pr?(pr.codiceCommessa||pr.name):"—")}</td>`:""}
        <td style="color:${st.col};font-weight:${st.forte?700:400}">${st.txt}</td>
        <td style="white-space:nowrap;color:${isTaskOverdue(t)?"#dc2626":"#111"}">${fmtData(t.dueDate)}${t.dueTime?" "+esc(t.dueTime):""}</td>
        <td style="text-transform:capitalize">${esc(t.priority||"media")}</td>
      </tr>`;
    };

    const sezione = (titolo, colore, items, conCommessa) => `
      <table class="grp">
        <thead>
          <tr><th colspan="${conCommessa?5:4}" class="gh" style="border-left:5px solid ${colore}">${esc(titolo)} <span class="cnt">${items.length}</span></th></tr>
          <tr class="ch"><th>Attività</th>${conCommessa?'<th style="width:92px">Commessa</th>':""}<th style="width:62px">Stato</th><th style="width:78px">Scadenza</th><th style="width:56px">Priorità</th></tr>
        </thead>
        <tbody>${[...items].sort(perScadenzaOrd).map(t=>riga(t,conCommessa)).join("")}</tbody>
      </table>`;

    let corpo = "";
    let riepilogo = "";
    if (groupBy === "progetto") {
      const chiavi = Object.keys(byProject).sort((x,y)=>{
        if (x==="__none__") return 1; if (y==="__none__") return -1;
        return nomeCommessa(projects[x]).localeCompare(nomeCommessa(projects[y]),"it",{numeric:true});
      });
      corpo = chiavi.map(k => sezione(k==="__none__" ? "Senza commessa" : nomeCommessa(projects[k]), projects[k]?.color||"#9ca3af", byProject[k], false)).join("");
      riepilogo = `raggruppate per commessa — ${chiavi.length} ${chiavi.length===1?"commessa":"commesse"}`;
    } else {
      const blocchi = [
        ["In ritardo","#dc2626",byDeadline.scadute],
        ["Oggi","#d97706",byDeadline.oggi],
        ["Questa settimana","#2563eb",byDeadline.settimana],
        ["In programma","#6b7280",byDeadline.inProgramma],
      ].filter(([,,it])=>it.length>0);
      corpo = blocchi.map(([t,c,it])=>sezione(t,c,it,true)).join("");
      riepilogo = "raggruppate per scadenza";
    }
    if (!corpo) corpo = '<div style="padding:20px;color:#6b7280;">Nessuna attività da stampare.</div>';

    win.document.write(`<!DOCTYPE html><html><head><meta charset="utf-8"/>
    <title>Le mie attività</title>
    <style>
      *{margin:0;padding:0;box-sizing:border-box;}
      body{font-family:Arial,sans-serif;font-size:10.5px;color:#111;padding:12mm 14mm;}
      @media screen{body{max-width:900px;margin:0 auto;background:#fff;box-shadow:0 2px 16px rgba(0,0,0,.08);}}
      @page{size:A4 portrait;margin:10mm;}
      @media print{.no-print{display:none;}body{padding:0;}}
      .hdr{display:flex;justify-content:space-between;align-items:center;border-bottom:3px solid #1e1b4b;padding-bottom:10px;margin-bottom:12px;}
      table.grp{width:100%;border-collapse:collapse;margin-bottom:14px;table-layout:fixed;}
      thead{display:table-header-group;}
      tr{page-break-inside:avoid;}
      th.gh{background:#f3f4f6;color:#1e1b4b;padding:7px 10px;font-size:12px;text-align:left;text-transform:uppercase;letter-spacing:.03em;}
      .cnt{display:inline-block;background:#ede9fe;color:#7c3aed;border-radius:10px;padding:0 7px;font-size:10px;margin-left:6px;}
      tr.ch th{background:#fff;color:#6b7280;padding:4px 8px;font-size:8.5px;text-align:left;text-transform:uppercase;border-bottom:1px solid #d1d5db;}
      td{padding:5px 8px;border-bottom:1px solid #e5e7eb;font-size:10.5px;vertical-align:top;word-wrap:break-word;}
      .sub{color:#7c3aed;font-weight:700;} .dip{color:#9ca3af;font-size:9px;} .off{text-decoration:line-through;color:#9ca3af;}
    </style></head><body>
      <div class="hdr">
        <div>${logoHtml}<div style="font-size:10px;color:#6b7280;margin-top:3px;">${esc(orgInfo?.nome||orgInfo?.name||"")}</div></div>
        <div style="text-align:right;">
          <div style="font-size:15px;font-weight:800;color:#1e1b4b;">✅ LE MIE ATTIVITÀ</div>
          <div style="font-size:10px;color:#6b7280;margin-top:2px;">${esc(currentUser?.displayName||"")} — ${new Date().toLocaleDateString("it-IT")}</div>
          <div style="font-size:10px;color:#6b7280;">${tasksFiltrati.length} attività ${riepilogo}${search.trim()?` — filtro: «${esc(search.trim())}»`:""}</div>
        </div>
      </div>
      ${corpo}
    </body></html>`);
    win.document.close();
    setTimeout(() => {
      const btn = win.document.createElement("button");
      btn.textContent = "🖨️ Stampa";
      btn.className = "no-print";
      btn.style.cssText = "position:fixed;top:16px;right:16px;padding:10px 20px;background:#7c3aed;color:#fff;border:none;border-radius:8px;font-size:14px;font-weight:700;cursor:pointer;font-family:Arial,sans-serif;box-shadow:0 4px 12px rgba(124,58,237,.3);z-index:9999;";
      btn.onclick = () => win.print();
      win.document.body.appendChild(btn);
    }, 50);
  };

  useEffect(() => {
    if (!currentUser) return;
    const qAT = query(collection(db,"tasks"),    where("assignedTo","==",currentUser.uid), where("organizationId","==",orgId));
    const qCT = query(collection(db,"tasks"),    where("createdBy", "==",currentUser.uid), where("organizationId","==",orgId));
    const qAS = query(collection(db,"subtasks"), where("assignedTo","==",currentUser.uid), where("organizationId","==",orgId));
    const qCS = query(collection(db,"subtasks"), where("createdBy", "==",currentUser.uid), where("organizationId","==",orgId));
    // Attività condivise con me da un collega (utils/condivisione.js)
    const qST = query(collection(db,"tasks"),    where("condivisaCon","array-contains",currentUser.uid), where("organizationId","==",orgId));
    let aT=[],cT=[],aS=[],cS=[],sT=[];
    const merge = async () => {
      const tm={}; sT.forEach(t=>{ tm[t.id]={...t,_isSubtask:false,_condivisa:true}; });
      [...aT,...cT].forEach(t=>{ tm[t.id]={...t,_isSubtask:false}; });
      const sm={}; [...aS,...cS].forEach(s=>{ sm[s.id]={...s,_isSubtask:true}; });
      const allT=Object.values(tm).filter(t=>t.status!=="completata"&&t.status!=="annullata");
      const allS=Object.values(sm).filter(s=>s.status!=="completata"&&s.status!=="annullata");
      const enriched = await Promise.all(allS.map(async s=>{
        if(s.parentTaskId){ try{ const snap=await getDoc(fsDoc(db,"tasks",s.parentTaskId)); if(snap.exists()) return {...s,projectId:snap.data().projectId}; }catch(e){} }
        return s;
      }));
      setTasks([...allT,...enriched]);
    };
    const u1=onSnapshot(qAT,s=>{aT=s.docs.map(d=>({id:d.id,...d.data()}));merge();});
    const u2=onSnapshot(qCT,s=>{cT=s.docs.map(d=>({id:d.id,...d.data()}));merge();});
    const u3=onSnapshot(qAS,s=>{aS=s.docs.map(d=>({id:d.id,...d.data()}));merge();});
    const u4=onSnapshot(qCS,s=>{cS=s.docs.map(d=>({id:d.id,...d.data()}));merge();});
    const u5=onSnapshot(qST,s=>{sT=s.docs.map(d=>({id:d.id,...d.data()}));merge();},err=>console.warn("Attività condivise non disponibili:",err?.code||err));
    return ()=>{u1();u2();u3();u4();u5();};
  }, [currentUser, orgId]);

  useEffect(() => {
    if (!orgId) return;
    getDoc(fsDoc(db,"organizations",orgId)).then(s=>{if(s.exists())setOrgInfo(s.data());});
  }, [orgId]);

  useEffect(() => {
    if (!orgId) return;
    getDocs(query(collection(db,"projects"),where("organizationId","==",orgId)))
      .then(snap=>{ const m={}; snap.docs.forEach(d=>{ m[d.id]={id:d.id,...d.data()}; }); setProjects(m); });
  }, [orgId]);

  // Nomi delle sezioni — solo per le commesse che compaiono davvero tra le mie attività,
  // non tutta l'organizzazione: la colonna Sezione è utile solo se qualcuno la mostra.
  useEffect(() => {
    if (!colonne.includes("sezione")) return;
    const projectIds = [...new Set(tasks.map(t=>t.projectId).filter(Boolean))];
    if (projectIds.length===0) return;
    Promise.all(projectIds.map(pid =>
      getDocs(query(collection(db,"sections"), where("projectId","==",pid)))
    )).then(risultati => {
      const m = {};
      risultati.forEach(snap => snap.docs.forEach(d => { m[d.id] = d.data().name; }));
      setSezioni(m);
    });
  }, [tasks, colonne]);

  const toggleCollapse = key => setCollapsed(p=>({...p,[key]:!p[key]}));

  const nascondiColonna = id => salvaColonne(colonne.filter(c=>c!==id));
  const spostaColonna = (id, direzione) => {
    const i = colonne.indexOf(id);
    const j = i + direzione;
    if (j<0 || j>=colonne.length) return;
    const nuove = [...colonne];
    [nuove[i], nuove[j]] = [nuove[j], nuove[i]];
    salvaColonne(nuove);
  };
  const aggiungiColonna = id => salvaColonne([...colonne, id]);
  const colonneNascoste = Object.keys(COLONNE_DEF).filter(id => !colonne.includes(id));

  const byDeadline = {
    scadute:     tasksFiltrati.filter(t=>isTaskOverdue(t)),
    oggi:        tasksFiltrati.filter(t=>t.dueDate===today&&!isTaskOverdue(t)),
    settimana:   tasksFiltrati.filter(t=>{ const d=daysUntilDue(t); return d>0&&d<=7&&t.dueDate!==today; }),
    inProgramma: tasksFiltrati.filter(t=>!t.dueDate||daysUntilDue(t)>7),
  };
  const byProject={};
  tasksFiltrati.forEach(t=>{ const k=t.projectId||"__none__"; if(!byProject[k]) byProject[k]=[]; byProject[k].push(t); });

  const handleSelect = t => {
    if(t._isSubtask){ setSelectedSubtask(t); setSelectedTask(null); }
    else{ setSelectedTask(t); setSelectedSubtask(null); }
  };

  const fmtDate = t => {
    if (!t.dueDate) return null;
    const overdue = isTaskOverdue(t);
    const diffDays = Math.round((new Date(t.dueDate) - new Date(today)) / 86400000);
    const months = ["Gen","Feb","Mar","Apr","Mag","Giu","Lug","Ago","Set","Ott","Nov","Dic"];
    const days   = ["Dom","Lun","Mar","Mer","Gio","Ven","Sab"];
    let label;
    if (t.dueDate===today && t.dueTime) label=`Oggi ${t.dueTime}`;
    else if (t.dueDate===today)          label="Oggi";
    else if (diffDays===1)               label="Domani";
    else if (diffDays===-1)              label=`Ieri${t.dueTime?" "+t.dueTime:""}`;
    else if (overdue)                    label=`${t.dueDate.split("-").reverse().join("/")}${t.dueTime?" "+t.dueTime:""}`;
    else {
      const dd=new Date(t.dueDate+"T12:00:00");
      label = diffDays<=6 ? days[dd.getDay()] : `${dd.getDate()} ${months[dd.getMonth()]}`;
      if (t.dueTime) label+=` ${t.dueTime}`;
    }
    const color = overdue?"#dc2626":diffDays<=0?"#dc2626":diffDays===1?"#d97706":diffDays<=3?"#2563eb":"#9ca3af";
    return { label, color };
  };

  const TaskRow = ({ task }) => {
    const overdue    = isTaskOverdue(task);
    const isSelected = selectedTask?.id===task.id||selectedSubtask?.id===task.id;
    const isSub      = task._isSubtask;

    return (
      <tr onClick={()=>handleSelect(task)}
        style={{ background:isSelected?"#faf5ff":"transparent", cursor:"pointer" }}
        onMouseOver={e=>{if(!isSelected) e.currentTarget.style.background="#f9fafb";}}
        onMouseOut={e=>{if(!isSelected) e.currentTarget.style.background=isSelected?"#faf5ff":"transparent";}}>
        <td style={{ padding: isSub?"6px 12px 6px 36px":"6px 12px", borderBottom:"1px solid #f3f4f6", overflow:"hidden" }}>
          <div style={{ display:"flex", alignItems:"center", gap:8 }}>
            <div style={{ width:8, height:8, borderRadius:"50%", flexShrink:0,
              background: isSub ? "#7c3aed" : "#f97316" }}/>
            <span style={{ fontSize:13, fontWeight:isSub?400:500,
              textDecoration: task.status==="completata"||task.status==="annullata" ? "line-through" : "none",
              color: task.status==="completata"||task.status==="annullata" ? "#9ca3af"
                   : task.status==="sospesa" ? "#d97706"
                   : overdue ? "#dc2626" : "#111827",
              overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>
              {task.title}
              {task._condivisa && <span title={`Condivisa con te`} style={{marginLeft:6,fontSize:10,fontWeight:700,color:"#7c3aed",background:"#f5f3ff",borderRadius:8,padding:"1px 6px"}}>👥 condivisa</span>}
            </span>
            {isSub && task.parentTaskTitle && (
              <span style={{ fontSize:11, color:"#c4b5fd", whiteSpace:"nowrap", flexShrink:0 }}>
                di "{task.parentTaskTitle}"
              </span>
            )}
          </div>
        </td>
        {colonne.map(cid => (
          <td key={cid} style={{ padding:"6px 12px", borderBottom:"1px solid #f3f4f6", borderLeft:"1px solid #f3f4f6", verticalAlign:"middle" }}>
            {COLONNE_DEF[cid].render(task, { projects, sezioni, fmtDate })}
          </td>
        ))}
      </tr>
    );
  };

    // ─── Header gruppo ────────────────────────────────────────────────
  const GroupHeader = ({ gKey, label, color, count }) => (
    <tr onClick={()=>toggleCollapse(gKey)}
      style={{ background:"#f9fafb", cursor:"pointer", borderBottom:"1px solid #e5e7eb" }}>
      <td colSpan={1+colonne.length} style={{ padding:"7px 12px" }}>
        <div style={{ display:"flex", alignItems:"center", gap:6 }}>
          {!collapsed[gKey]
            ? <ChevronDown size={13} color={color}/>
            : <ChevronRight size={13} color={color}/>}
          <span style={{ fontSize:12, fontWeight:700, color,
            textTransform:"uppercase", letterSpacing:"0.05em" }}>{label}</span>
          <span style={{ background:color+"22", color, borderRadius:10,
            padding:"1px 7px", fontSize:11, fontWeight:600 }}>{count}</span>
        </div>
      </td>
    </tr>
  );

  // ─── Header progetto ──────────────────────────────────────────────
  const ProjectHeader = ({ projectId, count, isFirst }) => {
    const proj = projects[projectId];
    return (
      <tr onClick={()=>toggleCollapse(projectId)}
        style={{ background:"#f9fafb", cursor:"pointer",
          borderBottom:"1px solid #e5e7eb",
          borderTop: isFirst ? "1px solid #e5e7eb" : "8px solid #fff" }}>
        <td colSpan={1+colonne.length} style={{ padding:"11px 12px" }}>
          <div style={{ display:"flex", alignItems:"center", gap:6 }}>
            {!collapsed[projectId]
              ? <ChevronDown size={13} color="#6b7280"/>
              : <ChevronRight size={13} color="#6b7280"/>}
            {proj
              ? <><div style={{width:10,height:10,borderRadius:3,
                  background:proj.color||"#7c3aed",flexShrink:0}}/>
                <CommessaLink commessa={proj} style={{fontSize:16,fontWeight:900,color:"#1e1b4b",
                  textTransform:"uppercase",letterSpacing:"0.03em",WebkitTextStroke:"0.4px #1e1b4b"}}>{proj.codiceCommessa ? `${proj.codiceCommessa} — ${proj.name}` : proj.name}</CommessaLink></>
              : <><Folder size={12} color="#9ca3af"/>
                <span style={{fontSize:12,fontWeight:700,color:"#9ca3af",
                  textTransform:"uppercase",letterSpacing:"0.05em"}}>Senza progetto</span></>
            }
            <span style={{background:"#ede9fe",color:"#7c3aed",borderRadius:10,
              padding:"1px 7px",fontSize:11,fontWeight:600}}>{count}</span>
          </div>
        </td>
      </tr>
    );
  };

  return (
    <div style={{ display:"flex", height:"100%", overflow:"hidden" }}>
      <div style={{ flex:1, display:"flex", flexDirection:"column", overflow:"hidden" }}>

        {/* Header pagina */}
        <div style={{ padding:"14px 20px 10px", borderBottom:"1px solid #e5e7eb", flexShrink:0 }}>
          <div style={{ marginBottom:10 }}>
            <h2 style={{ fontSize:18, fontWeight:700, marginBottom:2, display:"flex", alignItems:"center", gap:10, flexWrap:"wrap" }}>
              Le mie attività
              <button onClick={stampaPDF}
                style={{display:"flex",alignItems:"center",gap:5,padding:"5px 12px",border:"1px solid #7c3aed",borderRadius:7,background:"#faf5ff",color:"#7c3aed",fontSize:12,fontWeight:700,cursor:"pointer",fontFamily:"'Inter',sans-serif"}}>
                🖨️ Stampa PDF
              </button>
              <InsightButton onNavigate={onNavigate}/>
              <div style={{ display:"flex", background:"#f3f4f6", borderRadius:8, padding:3 }}>
                {[{id:"scadenza",label:"Per scadenza"},{id:"progetto",label:"Per progetto"}].map(g=>(
                  <button key={g.id} onClick={()=>setGroupBy(g.id)}
                    style={{ padding:"5px 12px", border:"none", borderRadius:6, fontSize:12,
                      fontWeight:500, cursor:"pointer", fontFamily:"'Inter',sans-serif",
                      background:groupBy===g.id?"#fff":"transparent",
                      color:groupBy===g.id?"#111827":"#6b7280",
                      boxShadow:groupBy===g.id?"0 1px 3px rgba(0,0,0,0.1)":"none",
                      transition:"all 0.15s" }}>
                    {g.label}
                  </button>
                ))}
              </div>
            </h2>
            <p style={{ fontSize:13, color:"#6b7280" }}>{tasksFiltrati.length} attività{search?" trovate":""}
              {nCondivise>0 && <button onClick={()=>setSoloCondivise(v=>!v)} style={{marginLeft:10,fontSize:12,fontWeight:600,padding:"2px 10px",borderRadius:12,cursor:"pointer",border:`1px solid ${soloCondivise?"#7c3aed":"#e5e7eb"}`,background:soloCondivise?"#f5f3ff":"#fff",color:soloCondivise?"#7c3aed":"#6b7280"}}>👥 Condivise con me ({nCondivise})</button>}
            </p>
          </div>
          {/* Barra ricerca */}
          <div style={{position:"relative",maxWidth:380}}>
            <input value={search} onChange={e=>setSearch(e.target.value)}
              placeholder="🔍 Cerca per attività, progetto o assegnatario..."
              style={{width:"100%",padding:"6px 30px 6px 12px",border:"1px solid #e5e7eb",borderRadius:8,
                fontSize:13,fontFamily:"'Inter',sans-serif",outline:"none",boxSizing:"border-box",background:"#fff"}}/>
            {search && <button onClick={()=>setSearch("")}
              style={{position:"absolute",right:8,top:"50%",transform:"translateY(-50%)",
                background:"none",border:"none",cursor:"pointer",color:"#9ca3af",fontSize:16,lineHeight:1}}>×</button>}
          </div>
        </div>

        {/* Tabella */}
        <div style={{ flex:1, overflowY:"auto", overflowX:"auto" }}>
          {tasks.length===0 ? (
            <div className="empty-state">
              <CheckCircle size={40} color="#d1d5db"/>
              <h3>Tutto in ordine!</h3>
              <p>Non hai attività assegnate in sospeso</p>
            </div>
          ) : (
            <table style={{
          width: "fit-content",
          minWidth: `${420 + colonne.reduce((s,c)=>s+COLONNE_DEF[c].width,0)}px`,
          borderCollapse: "collapse",
          tableLayout: "fixed"
        }}>
        <colgroup>
          <col style={{ width: "clamp(360px, 45vw, 620px)" }} />
          {colonne.map(cid => <col key={cid} style={{ width: `${COLONNE_DEF[cid].width}px` }} />)}
        </colgroup>
              {/* Intestazione colonne */}
              <thead style={{ position:"sticky", top:0, zIndex:5, background:"#fff" }}>
                <tr style={{ borderBottom:"1px solid #e5e7eb" }}>
                  <th style={{ padding:"6px 12px", fontSize:11, fontWeight:600,
                    color:"#9ca3af", textTransform:"uppercase", letterSpacing:"0.05em",
                    textAlign:"left" }}>Nome</th>
                  {colonne.map(cid => (
                    <th key={cid} style={{ padding:"6px 12px", fontSize:11, fontWeight:600,
                      color:"#9ca3af", textTransform:"uppercase", letterSpacing:"0.05em",
                      textAlign:"left", borderLeft:"1px solid #f3f4f6", position:"relative" }}>
                      <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", gap:4 }}>
                        <span>{COLONNE_DEF[cid].label}</span>
                        <button onClick={()=>setMenuColonna(menuColonna===cid?null:cid)}
                          style={{ background:"none", border:"none", cursor:"pointer", color:"#9ca3af",
                            padding:2, display:"flex", borderRadius:4 }}
                          onMouseOver={e=>e.currentTarget.style.background="#f3f4f6"}
                          onMouseOut={e=>e.currentTarget.style.background="none"}>
                          <MoreVertical size={13}/>
                        </button>
                      </div>
                      {menuColonna===cid && (
                        <div ref={menuRef} style={{ position:"absolute", top:"100%", right:0, marginTop:2,
                          background:"#fff", border:"1px solid #e5e7eb", borderRadius:8,
                          boxShadow:"0 4px 12px rgba(0,0,0,.08)", zIndex:10, minWidth:170,
                          textTransform:"none", letterSpacing:"normal", fontWeight:400 }}>
                          <button onClick={()=>{spostaColonna(cid,-1);setMenuColonna(null);}}
                            disabled={colonne.indexOf(cid)===0}
                            style={{ display:"flex", alignItems:"center", gap:8, width:"100%", padding:"8px 12px",
                              border:"none", background:"none", cursor:colonne.indexOf(cid)===0?"default":"pointer",
                              fontSize:12, color:colonne.indexOf(cid)===0?"#d1d5db":"#374151", textAlign:"left" }}>
                            <ArrowLeft size={13}/> Sposta a sinistra
                          </button>
                          <button onClick={()=>{spostaColonna(cid,1);setMenuColonna(null);}}
                            disabled={colonne.indexOf(cid)===colonne.length-1}
                            style={{ display:"flex", alignItems:"center", gap:8, width:"100%", padding:"8px 12px",
                              border:"none", background:"none", cursor:colonne.indexOf(cid)===colonne.length-1?"default":"pointer",
                              fontSize:12, color:colonne.indexOf(cid)===colonne.length-1?"#d1d5db":"#374151", textAlign:"left" }}>
                            <ArrowRight size={13}/> Sposta a destra
                          </button>
                          <button onClick={()=>{nascondiColonna(cid);setMenuColonna(null);}}
                            style={{ display:"flex", alignItems:"center", gap:8, width:"100%", padding:"8px 12px",
                              border:"none", borderTop:"1px solid #f3f4f6", background:"none", cursor:"pointer",
                              fontSize:12, color:"#dc2626", textAlign:"left" }}>
                            <EyeOff size={13}/> Nascondi questa colonna
                          </button>
                        </div>
                      )}
                    </th>
                  ))}
                  <th style={{ padding:"6px 8px", width:34, borderLeft:"1px solid #f3f4f6", position:"relative" }}>
                    <button onClick={()=>setMenuColonna(menuColonna==="+"?null:"+")}
                      title="Aggiungi colonna"
                      style={{ background:"none", border:"1px dashed #d1d5db", borderRadius:6, cursor:"pointer",
                        color:"#9ca3af", padding:3, display:"flex" }}>
                      <Plus size={13}/>
                    </button>
                    {menuColonna==="+" && (
                      <div ref={menuRef} style={{ position:"absolute", top:"100%", right:0, marginTop:2,
                        background:"#fff", border:"1px solid #e5e7eb", borderRadius:8,
                        boxShadow:"0 4px 12px rgba(0,0,0,.08)", zIndex:10, minWidth:170 }}>
                        {colonneNascoste.length===0 ? (
                          <div style={{ padding:"10px 12px", fontSize:12, color:"#9ca3af" }}>Nessuna colonna da aggiungere</div>
                        ) : colonneNascoste.map(cid => (
                          <button key={cid} onClick={()=>{aggiungiColonna(cid);setMenuColonna(null);}}
                            style={{ display:"flex", alignItems:"center", gap:8, width:"100%", padding:"8px 12px",
                              border:"none", background:"none", cursor:"pointer", fontSize:12, color:"#374151", textAlign:"left" }}>
                            <Plus size={13} color="#7c3aed"/> {COLONNE_DEF[cid].label}
                          </button>
                        ))}
                      </div>
                    )}
                  </th>
                </tr>
              </thead>

              <tbody>
                {groupBy==="scadenza" ? (<>
                  {byDeadline.scadute.length>0&&<><GroupHeader gKey="scadute" label="In ritardo" color="#dc2626" count={byDeadline.scadute.length}/>{!collapsed["scadute"]&&byDeadline.scadute.map(t=><TaskRow key={t.id} task={t}/>)}</>}
                  {byDeadline.oggi.length>0&&<><GroupHeader gKey="oggi" label="Oggi" color="#d97706" count={byDeadline.oggi.length}/>{!collapsed["oggi"]&&byDeadline.oggi.map(t=><TaskRow key={t.id} task={t}/>)}</>}
                  {byDeadline.settimana.length>0&&<><GroupHeader gKey="settimana" label="Questa settimana" color="#2563eb" count={byDeadline.settimana.length}/>{!collapsed["settimana"]&&byDeadline.settimana.map(t=><TaskRow key={t.id} task={t}/>)}</>}
                  {byDeadline.inProgramma.length>0&&<><GroupHeader gKey="inProgramma" label="In programma" color="#6b7280" count={byDeadline.inProgramma.length}/>{!collapsed["inProgramma"]&&byDeadline.inProgramma.map(t=><TaskRow key={t.id} task={t}/>)}</>}
                </>) : (
                  Object.entries(byProject).map(([pid,items],idx)=>(<React.Fragment key={pid}>
                    <ProjectHeader projectId={pid} count={items.length} isFirst={idx===0}/>
                    {!collapsed[pid]&&items.map(t=><TaskRow key={t.id} task={t}/>)}
                  </React.Fragment>))
                )}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {selectedTask && (
        <TaskDetailPanel task={selectedTask}
          projectName={selectedTask.projectId ? (()=>{ const p=projects[selectedTask.projectId]; return p ? (p.codiceCommessa ? `${p.codiceCommessa} — ${p.name}` : p.name) : "Commessa"; })() : "Senza progetto"}
          onClose={()=>{setSelectedTask(null);setSelectedSubtask(null);}}
          onUpdate={t=>setSelectedTask(t)}
          onSelectSubtask={st=>setSelectedSubtask(st)}
          selectedSubtaskId={selectedSubtask?.id}/>
      )}
      {selectedSubtask && (
        <SubtaskDetailPanel subtask={selectedSubtask}
          onClose={()=>setSelectedSubtask(null)}
          onUpdated={st=>setSelectedSubtask(st)}
          onDeleted={()=>setSelectedSubtask(null)}/>
      )}
    </div>
  );
}
